import { useMemo } from "react";
import type { MRT_ColumnDef } from "material-react-table";

import { APIPaginatedTable } from "@/components/common/api-paginated-table";
import type { APIPaginatedTableState } from "@/components/common/api-paginated-table/states";
import { appointmentStatusLabel } from "@/constants/appointments/appointments-constants";
import { SALES_LIST_STATUSES } from "@/constants/sales/sales-constants";
import { useGetSalesPaginated } from "@/hooks/sales/use-sales";
import {
  EM_DASH,
  saleStartTime,
  saleStylistColumn,
} from "@/mappers/sales/sale-to-table-row";
import { isoDate, isoTime } from "@/lib/utils";
import type { Sale } from "@/types/sales/api-types/sale-types";
import { useIntl } from "react-intl";


interface SalesTableProps {
  dateFrom: string;
  dateTo: string;
  onSaleClick: (sale: Sale) => void;
}

const SalesTable = ({ dateFrom, dateTo, onSaleClick }: SalesTableProps) => {
  const intl = useIntl();
  const columns = useMemo<MRT_ColumnDef<Sale>[]>(
    () => [
      {
        accessorKey: "client_name",
        header: "Client",
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const sale = row.original;
          return (
            <button
              type="button"
              onClick={() => onSaleClick(sale)}
              className="text-purple-500 hover:underline cursor-pointer"
            >
              {sale.client_name ?? "this client"}
            </button>
          );
        },
      },
      {
        id: "stylist",
        header: "Stylist",
        enableColumnFilter: false,
        Cell: ({ row }) => saleStylistColumn(row.original),
      },
      {
        accessorKey: "appointment_date",
        header: "Date",
        enableColumnFilter: false,
        Cell: ({ row }) => intl.formatDate(isoDate(row.original.appointment_date), { format: "short" }),
      },
      {
        id: "time",
        header: "Time",
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const start = saleStartTime(row.original);
          return start ? intl.formatTime(isoTime(start), { format: "clock" }) : EM_DASH;
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        enableColumnFilter: false,
        Cell: ({ row }) => appointmentStatusLabel(row.original.status),
      },
    ],
    [intl, onSaleClick]
  );

  return (
    <APIPaginatedTable
      columns={columns}
      reactQuery={useGetSalesPaginated}
      reactQueryFilters={(state: APIPaginatedTableState) => ({
        page: state.pagination.pageIndex,
        size: state.pagination.pageSize,
        date_from: dateFrom,
        date_to: dateTo,
        status: SALES_LIST_STATUSES,
      })}
      mrtTableOptions={{
        enableRowActions: false,
        enableColumnFilters: false,
      }}
      titleTopic="sales"
      contentTopic="walk-in sale"
    />
  );
};

export default SalesTable;
